import { FieldArray, Form, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { QcsButton } from '../common/basic/QcsButton';
import { SubmitButton } from '../common/form/SubmitButton';
import { QuestionItem } from './QuestionItem';
import { FaIcon } from '../common/FaIcon';
import { faAdd } from '@fortawesome/free-solid-svg-icons';
import styled, { CSSObject } from '@emotion/styled';
import { FormContainer } from '../common/form/FormContainer';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAppSelector } from '../../store';
import { selectQuestionnaireVersion } from '../../store/entities/questionnaireVersion';
import { QuestionDto } from '@api/models/questionnaire/QuestionDto';

export interface QuestionsForm {
  questions: QuestionDto[];
}

interface DraggableContainerProps {
  isDragging: boolean;
  selected?: boolean;
}

const QuestionsContainer = styled.div((): CSSObject => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
}));

const DraggableContainer = styled.div<DraggableContainerProps>(({ theme, isDragging, selected }): CSSObject => ({
  userSelect: 'none',
  marginBottom: '1rem',
  backgroundColor: selected ? (isDragging ? theme.palette.primary.dark : 'none') : isDragging ? theme.palette.secondary.dark : 'none',
  color: selected ? theme.palette.primary.contrastText : 'none',
  borderRadius: isDragging ? '5px' : '0px',
  fontFamily: theme.typography.fontFamily,
}));

interface SortableQuestionItemProps {
  id: string;
  index: number;
  editable: boolean;
  remove: (index: number) => void;
  onRemoveOldFile: (document: string) => void;
}

const SortableQuestionItem = ({ id, index, editable, remove, onRemoveOldFile }: SortableQuestionItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !editable,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <DraggableContainer isDragging={isDragging} ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <QuestionItem editable={editable} index={index} remove={remove} onRemoveOldFile={onRemoveOldFile} />
    </DraggableContainer>
  );
};

interface Props {
  onRemoveOldFile: (document: string) => void;
}

export const FormContent = ({ onRemoveOldFile }: Props) => {
  const { t } = useTranslation();
  const { values, setValues, validateForm, submitForm } = useFormikContext<QuestionsForm>();

  const questionnaireVersion = useAppSelector(selectQuestionnaireVersion);
  const editable = questionnaireVersion.editable ?? false;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = parseInt(active.id as string);
    const newIndex = parseInt(over.id as string);

    const result = arrayMove(values.questions, oldIndex, newIndex);
    setValues({ questions: result });
  };

  const handleAddItem = async (push: any) => {
    if (values.questions.length > 0) {
      const errors = await validateForm();
      if (!Object.keys(errors).length) await submitForm();
    }
    push({
      content: '',
      answers: [{ content: '', correct: true }],
    });
  };

  const questionIds = values.questions.map((_, i) => i.toString());

  return (
    <Form>
      <FormContainer>
        <FieldArray name="questions">
          {({ remove, push }: { remove: (index: number) => void; push: (obj: any) => void }) => (
            <QuestionsContainer>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={questionIds} strategy={verticalListSortingStrategy}>
                  <div>
                    {values.questions.map((_, i) => (
                      <SortableQuestionItem
                        key={i}
                        id={i.toString()}
                        index={i}
                        editable={editable}
                        remove={remove}
                        onRemoveOldFile={onRemoveOldFile}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              <QcsButton
                sx={{ width: 'fit-content' }}
                variant="outlined"
                disabled={!editable}
                startIcon={<FaIcon icon={faAdd} />}
                onClick={() => {
                  void handleAddItem(push);
                }}
              >
                {t('questionnaire.questions.addQuestion')}
              </QcsButton>
            </QuestionsContainer>
          )}
        </FieldArray>
        <SubmitButton disabled={!editable} />
      </FormContainer>
    </Form>
  );
};
